import { useEffect, useState } from "react";
import { toast } from "sonner";
import { TrendingUp, Wallet, Building2, Users, FileText } from "lucide-react";
import { StatCard } from "@/components/StatCard";
import { DataTable } from "@/components/fleet/DataTable";
import { api, errMsg, kina } from "@/lib/api";

const SettleBtn = ({ settled, onClick, testId }) => (
  <button
    data-testid={testId}
    onClick={onClick}
    className={`rounded-lg px-3 py-1 text-xs font-semibold transition-colors ${settled ? "bg-emerald-50 text-emerald-700 hover:bg-emerald-100" : "bg-blue-700 text-white hover:bg-blue-600"}`}
  >
    {settled ? "✓ Paid · undo" : "Mark paid"}
  </button>
);

const StatementLink = ({ to, testId }) => (
  <a href={to} target="_blank" rel="noreferrer" data-testid={testId} className="mr-2 inline-flex items-center gap-1 rounded-lg border border-blue-200 px-3 py-1 text-xs font-semibold text-blue-700 hover:bg-blue-50">
    <FileText className="h-3 w-3" /> Statement
  </a>
);

export const RevenuePanel = () => {
  const [d, setD] = useState(null);
  const load = () => api.get("/admin/revenue").then((r) => setD(r.data)).catch(() => {});
  useEffect(() => {
    load();
  }, []);
  const settle = async (party, party_id, date) => {
    try {
      const { data } = await api.post("/admin/settle", { party, party_id, date });
      toast.success(data.settled ? "Marked as paid" : "Marked as owing");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  if (!d) return <div className="h-64 animate-pulse rounded-2xl bg-white" />;
  return (
    <div className="space-y-10">
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard testId="admin-total-revenue" label="Platform revenue" value={kina(d.total)} icon={TrendingUp} tone="dark" hint="Driver fees + Comfort Taxi fees" />
        <StatCard testId="admin-driver-fees" label="From drivers (5%)" value={kina(d.driver_fees)} icon={Users} />
        <StatCard testId="admin-company-fees" label="From Comfort Taxi (5%)" value={kina(d.company_fees)} icon={Building2} />
        <StatCard testId="admin-outstanding" label="Outstanding" value={kina(d.outstanding)} icon={Wallet} hint={`Collected: ${kina(d.collected)}`} />
      </div>
      <section>
        <h3 className="mb-3 text-lg font-semibold text-slate-900">Daily driver fees</h3>
        <DataTable
          testId="admin-driver-fees-table"
          rows={d.driver_days}
          empty="No completed trips yet."
          cols={[
            ["Date", (r) => r.date],
            ["Driver", (r) => r.driver_name],
            ["Trips", (r) => r.trips],
            ["Earned", (r) => kina(r.gross)],
            ["Fee due", (r) => <b>{kina(r.driver_fee)}</b>],
            ["", (r) => <><StatementLink to={`/admin/statement/driver/${r.driver_id}/${r.date}`} testId={`statement-driver-${r.driver_id}-${r.date}`} /><SettleBtn testId={`settle-driver-${r.driver_id}-${r.date}`} settled={r.settled} onClick={() => settle("driver", r.driver_id, r.date)} /></>],
          ]}
        />
      </section>
      <section>
        <h3 className="mb-3 text-lg font-semibold text-slate-900">Daily Comfort Taxi fees</h3>
        <DataTable
          testId="admin-company-fees-table"
          rows={d.company_days}
          empty="No completed trips yet."
          cols={[
            ["Date", (r) => r.date],
            ["Trips", (r) => r.trips],
            ["Fleet fares", (r) => kina(r.gross)],
            ["Fee due", (r) => <b>{kina(r.company_fee)}</b>],
            ["", (r) => <><StatementLink to={`/admin/statement/company/comfort/${r.date}`} testId={`statement-company-${r.date}`} /><SettleBtn testId={`settle-company-${r.date}`} settled={r.settled} onClick={() => settle("company", "comfort", r.date)} /></>],
          ]}
        />
      </section>
    </div>
  );
};
